import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { FaultListService, FaultWikiPageParams } from "../../services/api/fault-list/fault-list.service";
import { TableLazyLoadEvent, TableModule } from "@openng/optimus-ui/table";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { FormsModule } from "@angular/forms";
import {
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  first,
  forkJoin,
  Subject,
  switchMap,
} from "rxjs";
import { ControllerCatalogService } from "../../services/api/controller-catalog/controller-catalog.service";
import { CONTROLLER_CATALOG_ITEM_TYPES } from "../../enums/controllerCatalogItemTypes";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { PAGINATOR_CONSTANTS } from "../../constants/paginator";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { ControllerManufacturer } from "../../models/api/fault/controllerManufacturer";
import { ControllerModel } from "../../models/api/fault/controllerModel";
import { Translation, WikiFault } from "../../models/api/fault/fault";
import { Column } from "../../models/ui/tableColumn";
import { AuthService } from "../../services/api/auth/auth.service";
import { WIKI_ACCESS_LEVEL } from "../../enums/wikiAccessLevels";
import { SelectModule } from "@openng/optimus-ui/select";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  standalone: true,
  selector: "app-wiki",
  imports: [
    TableModule,
    TranslateModule,
    ButtonComponent,
    SelectModule,
    FormsModule,
    InputTextModule,
    HasPermissionDirective,
    TimeoutComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: "./wiki.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./wiki.component.scss",
})
export class WikiComponent implements OnInit {
  public paginatorOptions = PAGINATOR_CONSTANTS;
  private readonly faultListService = inject(FaultListService);
  private readonly controllerCatalogService = inject(ControllerCatalogService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);
  public loading = signal(true);
  public tableLoading = signal(false);
  public timeOut = createTimeoutSignal(this.loading);
  public WIKI_COLS: Column[] = [
    {
      field: "faultCode",
      header: "wiki.table.fault_code",
    },
    {
      field: "faultType",
      header: "wiki.table.fault_type",
    },
    {
      field: "faultText",
      header: "wiki.table.fault_text",
    },
    {
      field: "faultList",
      header: "wiki.table.fault_list",
    },
    {
      field: "manufacturers",
      header: "wiki.table.manufacturers",
      sortable: false,
    },
    {
      field: "models",
      header: "wiki.table.models",
      sortable: false,
    },
    {
      field: "fixLink",
      header: "wiki.table.fix_link",
      sortable: false,
    },
  ];

  public manufacturers: ControllerManufacturer[] = [];

  private allModels: ControllerModel[] = [];
  public filteredModels: ControllerModel[] = [];

  public wikiFaults: WikiFault[] = [];
  public totalRecords = 0;
  public first = 0;
  public rows = this.paginatorOptions.DEFAULT_ROWS;

  public selectedManufacturer: string | null = null;
  public selectedModel: string | null = null;

  private searchValue = "";
  private sortBy?: FaultWikiPageParams["sortBy"];
  private sortOrder: FaultWikiPageParams["sortOrder"] = "asc";
  private readonly searchChanges = new Subject<string>();
  private readonly wikiPageRequests = new Subject<{ page: number; pageSize: number }>();

  public ngOnInit() {
    this.searchChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchValue) => {
        this.searchValue = searchValue;
        this.loadFirstPage();
      });

    this.wikiPageRequests
      .pipe(
        switchMap(({ page, pageSize }) =>
          defer(() => {
            this.tableLoading.set(true);
            return this.faultListService.getFaultWikiData(this.getWikiPageParams(page, pageSize)).pipe(
              catchError(() => {
                this.loading.set(false);
                return EMPTY;
              }),
              finalize(() => this.tableLoading.set(false))
            );
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.wikiFaults = response.items;
        this.totalRecords = response.total;
        this.loading.set(false);
      });

    const manufacturers$ = this.controllerCatalogService
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER)
      .pipe(first());

    const models$ = this.controllerCatalogService.getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MODEL).pipe(first());

    forkJoin([manufacturers$, models$]).subscribe({
      next: ([manufacturers, models]) => {
        if (manufacturers) {
          this.manufacturers = manufacturers as ControllerManufacturer[];
        }
        if (models) {
          this.allModels = models as ControllerModel[];
          this.filteredModels = this.allModels;
        }
        this.loadFirstPage();
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  /**
   * Updates the server-side table search based on user input.
   * @param event The input event containing the target HTMLInputElement.
   */
  public filterGlobal(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchChanges.next(target.value.trim());
  }
  /**
   * Opens a given fix link in a new tab.
   * @param fault The wiki fault to extract the link from.
   */
  public openFixLink(fault: WikiFault) {
    const matchingTranslation = fault.translations?.find((t) => t.languageCode === this.translate.currentLang);

    let linkSource: Translation;
    if (matchingTranslation) {
      linkSource = matchingTranslation;
    } else if (fault.translations?.length) {
      linkSource = fault.translations[0];
    } else {
      return;
    }
    let fixLink: string | null | undefined;
    switch (this.authService.getWikiAccessLevel()) {
      case WIKI_ACCESS_LEVEL.LITE:
        fixLink = linkSource.fixLinkLite;
        break;
      case WIKI_ACCESS_LEVEL.EXPERT:
        fixLink = linkSource.fixLinkExpert;
        break;
      default:
        fixLink = linkSource.fixLinkLite;
        break;
    }

    if (fixLink) {
      window.open(fixLink, "_blank");
    }
  }

  getFaultDescription(fault: WikiFault) {
    const matchingTranslation = fault.translations?.find((t) => t.languageCode === this.translate.currentLang);
    if (matchingTranslation) {
      return matchingTranslation.faultText;
    } else if (fault.translations?.length) {
      return fault.translations[0].faultText;
    } else {
      return "";
    }
  }

  /**
   * Updates the filtered list of models based on the selected manufacturer.
   * Reloads the first wiki page with the selected manufacturer filter.
   */
  public onManufacturerChange() {
    if (this.selectedManufacturer !== null && this.selectedManufacturer !== undefined) {
      this.filteredModels = this.allModels.filter((item) => item.manufacturer.id === this.selectedManufacturer);
      if (this.selectedModel && !this.filteredModels.map((m) => m.id).includes(this.selectedModel)) {
        this.selectedModel = null;
      }
    } else {
      this.filteredModels = this.allModels;
    }
    this.loadFirstPage();
  }
  /**
   * Triggered when the selected model changes.
   * Reloads the first wiki page with the selected model filter.
   */

  public onModelChange() {
    this.loadFirstPage();
  }

  public onLazyLoad(event: TableLazyLoadEvent) {
    const pageSize = event.rows || this.paginatorOptions.DEFAULT_ROWS;
    const page = Math.floor((event.first || 0) / pageSize) + 1;

    this.first = event.first || 0;
    this.rows = pageSize;
    this.sortBy = this.toWikiSortField(event.sortField);
    this.sortOrder = event.sortOrder === -1 ? "desc" : "asc";
    this.loadWikiFaults(page, pageSize);
  }

  private loadFirstPage() {
    this.first = 0;
    this.loadWikiFaults(1, this.rows);
  }

  private loadWikiFaults(page: number, pageSize: number) {
    this.wikiPageRequests.next({ page, pageSize });
  }

  private getWikiPageParams(page: number, pageSize: number): FaultWikiPageParams {
    return {
      page,
      pageSize,
      search: this.searchValue,
      manufacturerId: this.selectedManufacturer || undefined,
      modelId: this.selectedModel || undefined,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    };
  }

  private toWikiSortField(sortField: TableLazyLoadEvent["sortField"]): FaultWikiPageParams["sortBy"] | undefined {
    const field = Array.isArray(sortField) ? sortField[0] : sortField;
    const sortableFields: FaultWikiPageParams["sortBy"][] = ["faultCode", "faultType", "faultText", "faultList"];

    return sortableFields.find((sortableField) => sortableField === field);
  }
}
